import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add,
  Delete,
  Save,
  Cancel,
} from '@mui/icons-material';
import { PhaseDefinition } from '../../types';

interface PhaseConfigurationPanelProps {
  phase?: PhaseDefinition;
  onSave: (phase: PhaseDefinition) => void;
  onCancel: () => void;
  existingPhaseNames: string[];
}

const PhaseConfigurationPanel: React.FC<PhaseConfigurationPanelProps> = ({
  phase,
  onSave,
  onCancel,
  existingPhaseNames,
}) => {
  const [phaseName, setPhaseName] = useState('');
  const [description, setDescription] = useState('');
  const [entryCriteria, setEntryCriteria] = useState('');
  const [exitActions, setExitActions] = useState<string[]>(['']);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with phase data if editing
  useEffect(() => {
    if (phase) {
      setPhaseName(phase.name);
      setDescription(phase.description || '');
      setEntryCriteria(phase.entryCriteria || '');
      setExitActions(
        phase.exitActions.length > 0 ? [...phase.exitActions] : ['']
      );
    } else {
      // Reset form for new phase
      setPhaseName('');
      setDescription('');
      setEntryCriteria('');
      setExitActions(['']);
    }
    setErrors({});
  }, [phase]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Phase name required
    if (!phaseName.trim()) {
      newErrors.phaseName = 'Phase name is required';
    } else {
      // Check uniqueness (case-insensitive, excluding current phase)
      const normalizedName = phaseName.toLowerCase().trim();
      const isDuplicate = existingPhaseNames
        .filter(name => name.toLowerCase() !== (phase?.name.toLowerCase() || ''))
        .some(name => name.toLowerCase() === normalizedName);
      
      if (isDuplicate) {
        newErrors.phaseName = 'Phase name must be unique';
      }
    }

    // Exit actions validation: if any action exists, it must not be empty
    const hasEmptyActions = exitActions.some(action => action.trim() === '');
    const hasNonEmptyActions = exitActions.some(action => action.trim() !== '');
    
    if (hasEmptyActions && hasNonEmptyActions) {
      newErrors.exitActions = 'Exit actions cannot have empty entries';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    // Filter out empty exit actions
    const filteredExitActions = exitActions.filter(action => action.trim() !== '');

    const phaseData: PhaseDefinition = {
      phaseId: phase?.phaseId || Date.now(),
      name: phaseName.trim(),
      description: description.trim() || undefined,
      entryCriteria: entryCriteria.trim() || undefined,
      exitActions: filteredExitActions,
      displayOrder: phase?.displayOrder || 0,
    };

    onSave(phaseData);
  };

  const handleAddExitAction = () => {
    setExitActions([...exitActions, '']);
  };

  const handleRemoveExitAction = (index: number) => {
    if (exitActions.length > 1) {
      const newActions = exitActions.filter((_, i) => i !== index);
      setExitActions(newActions);
    } else {
      // Keep at least one empty field
      setExitActions(['']);
    }
  };

  const handleExitActionChange = (index: number, value: string) => {
    const newActions = [...exitActions];
    newActions[index] = value;
    setExitActions(newActions);
    
    // Clear error when user starts typing
    if (errors.exitActions) {
      setErrors({ ...errors, exitActions: '' });
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {phase ? 'Edit Phase' : 'Add New Phase'}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        {/* Phase Name */}
        <TextField
          label="Phase Name"
          value={phaseName}
          onChange={(e) => {
            setPhaseName(e.target.value);
            if (errors.phaseName) {
              setErrors({ ...errors, phaseName: '' });
            }
          }}
          required
          fullWidth
          error={!!errors.phaseName}
          helperText={errors.phaseName}
          autoFocus
        />

        {/* Description */}
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder="Optional description of this phase"
        />

        {/* Entry Criteria */}
        <TextField
          label="Entry Criteria"
          value={entryCriteria}
          onChange={(e) => setEntryCriteria(e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder="Optional criteria that must be met before entering this phase"
        />

        {/* Exit Actions */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Exit Actions</Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={handleAddExitAction}
            >
              Add Action
            </Button>
          </Box>
          
          {exitActions.map((action, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                value={action}
                onChange={(e) => handleExitActionChange(index, e.target.value)}
                placeholder="Enter exit action"
                fullWidth
                size="small"
                error={!!errors.exitActions}
              />
              <IconButton
                onClick={() => handleRemoveExitAction(index)}
                color="error"
                disabled={exitActions.length === 1 && exitActions[0] === ''}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
          
          {errors.exitActions && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.exitActions}
            </Alert>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Actions that occur when exiting this phase (optional)
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
          >
            Save Phase
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default PhaseConfigurationPanel;

